import type { FC } from 'react';
import { Controller } from 'react-hook-form';
import { SearchableInputSelect } from '@components/SearchableInputSelect';
import { Alert, AlertDescription } from '@components/ui/alert';
import { ResourceState } from '@domain/schemas/contract.schema';
import { endorsementQueries } from '@queries/fetchEndorsements';
import { isEmptyValue } from '@root/helpers';
import { useQuery } from '@tanstack/react-query';
import { Info } from 'lucide-react';

import type { StepProps } from '../types';
import { getBlankEndorsementTemplateValues, getValuesFromEndorsement } from '../utils';

export const EndorsementSelectionStep: FC<StepProps> = ({ control, formValues, isReadOnly = false, setValue }) => {
  const contractId = formValues.duplicateSourceContractId;

  const { data: endorsements = [], isLoading } = useQuery({
    ...endorsementQueries.forSubmission(contractId ?? ''),
    enabled: !isEmptyValue(contractId),
  });

  const endorsementOptions = endorsements
    .filter((endorsement) => endorsement.status !== ResourceState.ARCHIVED)
    .map((endorsement) => ({
      name: [endorsement.reference, endorsement.internal_description].filter(Boolean).join(' - '),
      value: endorsement.id,
    }));

  const selectEndorsement = (endorsementId?: string) => {
    const selectedEndorsement = endorsements.find((endorsement) => endorsement.id === endorsementId);
    const values = selectedEndorsement
      ? getValuesFromEndorsement(selectedEndorsement)
      : getBlankEndorsementTemplateValues();
    setValue?.('effective_date', values.effective_date);
    setValue?.('expiry_date', values.expiry_date);
    setValue?.('internal_description', values.internal_description);
    setValue?.('premium_change', values.premium_change);
    setValue?.('reason', values.reason);
    setValue?.('reference', values.reference);
    setValue?.('type', values.type);
    setValue?.('additional_premium', values.additional_premium);
  };

  return (
    <div className="flex flex-col gap-4">
      <Controller
        control={control}
        name="duplicateSourceEndorsementId"
        render={({ field: { ref: _ref, onChange, ...field }, fieldState: { error } }) => (
          <SearchableInputSelect
            {...field}
            isDisabled={isReadOnly}
            isLoading={isLoading}
            noOptionsLeftLabel="No endorsements available to duplicate"
            labelText="Select the endorsement to duplicate"
            placeholder="Search endorsements"
            options={endorsementOptions}
            value={formValues.duplicateSourceEndorsementId}
            errors={error?.message}
            dataTestId="select:duplicate-source-endorsement-id"
            onChange={(value, name) => {
              onChange(value, name);
              selectEndorsement(value as string | undefined);
            }}
          />
        )}
      />

      <Alert variant="info">
        <Info className="text-gray-400!" />
        <AlertDescription>
          Please review the duplicated endorsement to ensure all changes have been copied as intended.
        </AlertDescription>
      </Alert>
    </div>
  );
};
